import { useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { toast } from 'react-toastify';
import type { SniperGroup, SniperGroupMode } from '../../types/sniper.types';
import {
  useStartGroup,
  useStopGroup,
  usePanicGroup,
  useClearGroupHalt,
} from '../../api/useSniper';
import {
  deriveSniperGroupBadge,
  sniperStartConfirmSeverity,
} from '../../utils/sniperGroupState';

/**
 * Per-group Sniper controls — a Start split-button (test | normal mode), a
 * Stop, a Panic, and (while halted) a Clear-halt. Mirrors the opener's
 * `GroupPodControls` conventions: compact status chip + a `ButtonGroup` start
 * split-button whose dropdown picks the mode, warning = soft, error = hard.
 * `normal` is the live real-money account so its confirm is red/hard; `test` is
 * the low-balance test account so its confirm is amber/softer. Panic is
 * immediate (confirm). Clear-halt does NOT resume — the group stays stopped.
 */
type StartConfirm = { kind: 'start'; mode: SniperGroupMode };
type Confirm = StartConfirm | { kind: 'stop' } | { kind: 'panic' };

export function SniperGroupControls({ group }: { group: SniperGroup }) {
  const startGroup = useStartGroup();
  const stopGroup = useStopGroup();
  const panicGroup = usePanicGroup();
  const clearHalt = useClearGroupHalt();

  const [startMenuAnchor, setStartMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [confirm, setConfirm] = useState<Confirm | null>(null);

  const badge = deriveSniperGroupBadge(group);
  const isBusy =
    startGroup.isLoading ||
    stopGroup.isLoading ||
    panicGroup.isLoading ||
    clearHalt.isLoading;

  const canStart = !group.running && !group.panicked;
  const canStop = group.running && !group.panicked;
  const canPanic = !group.panicked;

  const runConfirmed = () => {
    if (!confirm) return;
    if (confirm.kind === 'start') {
      startGroup.mutate(
        { groupId: group._id, mode: confirm.mode },
        {
          onError: () => {
            toast.error('Failed to start group.');
          },
        },
      );
    } else if (confirm.kind === 'stop') {
      stopGroup.mutate(group._id, {
        onError: () => {
          toast.error('Failed to stop group.');
        },
      });
    } else if (confirm.kind === 'panic') {
      panicGroup.mutate(group._id, {
        onError: () => {
          toast.error('Failed to panic group.');
        },
      });
    }
    setConfirm(null);
  };

  const startMode = (mode: SniperGroupMode) => {
    setStartMenuAnchor(null);
    setConfirm({ kind: 'start', mode });
  };

  const confirmCopy = (): {
    title: string;
    body: string;
    cta: string;
    color: 'error' | 'warning';
  } => {
    if (confirm?.kind === 'start') {
      const normal = confirm.mode === 'normal';
      return {
        title: normal
          ? `Start "${group.name}" in NORMAL mode?`
          : `Start "${group.name}" in TEST mode?`,
        body: normal
          ? 'NORMAL mode trades against the LIVE real-money account. Enabled, ' +
            'ARMED markets will fire live sweeps on the next qualifying ' +
            'incident. Continue?'
          : 'TEST mode trades against the separate low-balance TEST account. ' +
            'Enabled, ARMED markets will fire test sweeps. Continue?',
        cta: normal ? 'Start (live account)' : 'Start (test account)',
        color: sniperStartConfirmSeverity(confirm.mode),
      };
    }
    if (confirm?.kind === 'stop') {
      return {
        title: `Stop "${group.name}"?`,
        body:
          'Stops the group: its markets stop firing until it is Started ' +
          'again. Resting orders are handled by the engine drain.',
        cta: 'Stop group',
        color: 'warning',
      };
    }
    return {
      title: `PANIC "${group.name}"?`,
      body:
        'Immediately halts the group and cancels its orders. The group stays ' +
        'halted until you clear the halt AND Start it again.',
      cta: 'Panic group',
      color: 'error',
    };
  };

  const copy = confirmCopy();

  return (
    <>
      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
        <Chip
          size="small"
          label={badge.label}
          color={badge.severity}
          variant={badge.severity === 'default' ? 'outlined' : 'filled'}
          sx={{ fontSize: '0.65rem', height: 20, fontWeight: 700 }}
        />

        <Tooltip
          title={
            group.panicked
              ? 'Group is halted — clear the halt first'
              : canStart
                ? `Start "${group.name}"`
                : 'Group already running'
          }
          placement="top"
        >
          <span>
            <ButtonGroup variant="outlined" color="success" size="small">
              <Button
                startIcon={
                  startGroup.isLoading ? (
                    <CircularProgress size={12} color="inherit" />
                  ) : (
                    <PlayArrowIcon />
                  )
                }
                onClick={() => setConfirm({ kind: 'start', mode: 'test' })}
                disabled={isBusy || !canStart}
                sx={{ fontSize: '0.7rem' }}
              >
                Start
              </Button>
              <Button
                size="small"
                disabled={isBusy || !canStart}
                onClick={(e) => setStartMenuAnchor(e.currentTarget)}
                sx={{ px: 0.25, minWidth: 0 }}
                aria-label="Start mode options"
              >
                <ArrowDropDownIcon fontSize="small" />
              </Button>
            </ButtonGroup>
          </span>
        </Tooltip>
        <Menu
          anchorEl={startMenuAnchor}
          open={Boolean(startMenuAnchor)}
          onClose={() => setStartMenuAnchor(null)}
        >
          <MenuItem
            onClick={() => startMode('test')}
            sx={{ alignItems: 'flex-start', maxWidth: 300 }}
          >
            <Box>
              <Typography
                sx={{ fontSize: '0.8rem', color: 'warning.main', fontWeight: 600 }}
              >
                Start in test mode
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: 'grey.500' }}>
                Trades against the separate low-balance TEST account.
              </Typography>
            </Box>
          </MenuItem>
          <MenuItem
            onClick={() => startMode('normal')}
            sx={{ alignItems: 'flex-start', maxWidth: 300 }}
          >
            <Box>
              <Typography
                sx={{ fontSize: '0.8rem', color: 'error.main', fontWeight: 600 }}
              >
                Start in normal mode
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: 'grey.500' }}>
                Trades against the LIVE real-money account.
              </Typography>
            </Box>
          </MenuItem>
        </Menu>

        <Tooltip
          title={canStop ? `Stop "${group.name}"` : 'Group is not running'}
          placement="top"
        >
          <span>
            <Button
              variant="outlined"
              color="warning"
              size="small"
              startIcon={
                stopGroup.isLoading ? (
                  <CircularProgress size={12} color="inherit" />
                ) : (
                  <PowerSettingsNewIcon />
                )
              }
              onClick={() => setConfirm({ kind: 'stop' })}
              disabled={isBusy || !canStop}
              sx={{ fontSize: '0.7rem' }}
            >
              Stop
            </Button>
          </span>
        </Tooltip>

        <Tooltip
          title={`Immediately halt "${group.name}" + cancel its orders`}
          placement="top"
        >
          <span>
            <Button
              variant="contained"
              color="error"
              size="small"
              startIcon={
                panicGroup.isLoading ? (
                  <CircularProgress size={12} color="inherit" />
                ) : (
                  <WarningAmberIcon />
                )
              }
              onClick={() => setConfirm({ kind: 'panic' })}
              disabled={isBusy || !canPanic}
              sx={{ fontSize: '0.7rem' }}
            >
              Panic
            </Button>
          </span>
        </Tooltip>

        {group.panicked && (
          <Tooltip
            title="Clear this group's halt. Does NOT resume — Start it after."
            placement="top"
          >
            <span>
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={
                  clearHalt.isLoading ? (
                    <CircularProgress size={12} color="inherit" />
                  ) : (
                    <LockOpenIcon />
                  )
                }
                onClick={() =>
                  clearHalt.mutate(group._id, {
                    onError: () => {
                      toast.error('Failed to clear halt.');
                    },
                  })
                }
                disabled={clearHalt.isLoading}
                sx={{ fontSize: '0.7rem' }}
              >
                Clear halt
              </Button>
            </span>
          </Tooltip>
        )}
      </Stack>

      <Dialog
        open={confirm !== null}
        onClose={() => setConfirm(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '1rem' }}>{copy.title}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.85rem' }}>{copy.body}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button size="small" onClick={() => setConfirm(null)}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            color={copy.color}
            onClick={runConfirmed}
          >
            {copy.cta}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
